import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowRight, Download, Copy, Share2, FileVideo, Mic, Languages, PlayCircle, PauseCircle, UploadCloud, Youtube, Instagram } from "lucide-react";

const LipSyncPage = () => {
  const [inputText, setInputText] = useState("");
  const [translatedText, setTranslatedText] = useState("");
  const [selectedCharacter, setSelectedCharacter] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [showShareOptions, setShowShareOptions] = useState(false);

  const characters = [
    { id: "char1", name: "Emma", type: "Realistic" },
    { id: "char2", name: "John", type: "Realistic" },
    { id: "char3", name: "Sophia", type: "Realistic" },
    { id: "char4", name: "Michael", type: "Realistic" },
    { id: "char5", name: "Cartoon Cat", type: "Cartoon" },
    { id: "char6", name: "Cartoon Dog", type: "Cartoon" },
    { id: "char7", name: "Anime Girl", type: "Anime" },
    { id: "char8", name: "Anime Boy", type: "Anime" },
  ];

  const handleTranslate = () => {
    // Mock translation (in a real app, this would call an API)
    const mockTranslation = inputText 
      ? `This is a simulated English translation of your Portuguese text. The original was: "${inputText}"`
      : "";
    
    setTranslatedText(mockTranslation);
    toast.success("Texto traduzido com sucesso!");
  };

  const handleStartRecording = () => {
    setIsRecording(true);
    
    // Simulate recording process
    setTimeout(() => {
      setIsRecording(false);
      toast.success("Áudio gravado com sucesso!");
    }, 3000);
  };

  const handleGenerateVideo = () => {
    if (!selectedCharacter) {
      toast.error("Por favor, selecione um personagem primeiro!");
      return;
    }
    
    if (!translatedText) {
      toast.error("Por favor, traduza o texto primeiro!");
      return;
    }
    
    toast.info("Gerando vídeo, por favor aguarde...");
    
    // Simulate video generation process
    setTimeout(() => {
      setVideoUrl("https://placehold.co/600x400/jpeg");
      toast.success("Vídeo gerado com sucesso!");
    }, 3000);
  };

  const handlePlayVideo = () => {
    setIsPlaying(!isPlaying);
  };

  const handleDownloadVideo = () => {
    toast.success("Download do vídeo iniciado!");
    // In a real app, this would trigger a download
  };

  const handleShareVideo = () => {
    setShowShareOptions(true);
  };

  const handleShare = (platform: string) => {
    toast.success(`Compartilhado no ${platform}!`);
    setShowShareOptions(false);
  };

  const handleCopyText = () => {
    navigator.clipboard.writeText(translatedText);
    toast.success("Texto copiado para a área de transferência!");
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Criando Áudio e Vídeo no LipSync
          </h1>
          <p className="text-muted-foreground max-w-3xl mx-auto mb-6">
            Aprenda a criar vídeos sincronizados com áudio usando LipSync. Escolha personagens, traduza texto do português para inglês, e compartilhe seus vídeos nas redes sociais.
          </p>
          <div className="max-w-5xl mx-auto rounded-lg overflow-hidden shadow-lg">
            <img 
              src="https://storage.googleapis.com/fenado-ai-farm-public/generated/6780dd51-ccaf-4e6d-bf89-d6a4410ef1f6.webp"
              alt="Processo de criação de vídeo com LipSync"
              className="w-full h-auto"
            />
          </div>
        </div>

        <Tabs defaultValue="tutorial" className="max-w-5xl mx-auto">
          <TabsList className="grid grid-cols-3 mb-8">
            <TabsTrigger value="tutorial">Tutorial</TabsTrigger>
            <TabsTrigger value="create">Criar Vídeo</TabsTrigger>
            <TabsTrigger value="share">Compartilhar</TabsTrigger>
          </TabsList>

          <TabsContent value="tutorial">
            <Card>
              <CardHeader>
                <CardTitle>Como usar o LipSync</CardTitle>
                <CardDescription>
                  Siga este tutorial passo a passo para criar seu vídeo com LipSync
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">1. Acesse o site do LipSync</h3>
                  <p className="text-muted-foreground">
                    Primeiro, acesse o site oficial do LipSync em <a href="https://lipsync.video/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">lipsync.video</a>
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">2. Escolha o personagem</h3>
                  <p className="text-muted-foreground">
                    Selecione um dos personagens disponíveis para o seu vídeo. Existem opções realistas e de desenho animado.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                    <div className="border rounded-lg p-4 text-center hover:bg-muted cursor-pointer">
                      <div className="w-20 h-20 bg-primary/20 rounded-full mx-auto mb-2 flex items-center justify-center">
                        <span className="text-2xl">👩</span>
                      </div>
                      <p>Emma</p>
                    </div>
                    <div className="border rounded-lg p-4 text-center hover:bg-muted cursor-pointer">
                      <div className="w-20 h-20 bg-primary/20 rounded-full mx-auto mb-2 flex items-center justify-center">
                        <span className="text-2xl">👨</span>
                      </div>
                      <p>John</p>
                    </div>
                    <div className="border rounded-lg p-4 text-center hover:bg-muted cursor-pointer">
                      <div className="w-20 h-20 bg-primary/20 rounded-full mx-auto mb-2 flex items-center justify-center">
                        <span className="text-2xl">🐱</span>
                      </div>
                      <p>Cartoon Cat</p>
                    </div>
                    <div className="border rounded-lg p-4 text-center hover:bg-muted cursor-pointer">
                      <div className="w-20 h-20 bg-primary/20 rounded-full mx-auto mb-2 flex items-center justify-center">
                        <span className="text-2xl">👧</span>
                      </div>
                      <p>Anime Girl</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">3. Prepare seu texto em português</h3>
                  <p className="text-muted-foreground">
                    Escreva o texto que deseja que o personagem fale. Lembre-se de manter o texto com menos de 500 caracteres para melhores resultados.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">4. Traduza para inglês</h3>
                  <p className="text-muted-foreground">
                    Use o Google Tradutor para converter seu texto em português para inglês. Acesse <a href="https://translate.google.com.br/?sl=es&tl=en&op=translate" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Tradutor</a>
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">5. Gere o vídeo</h3>
                  <p className="text-muted-foreground">
                    Cole o texto traduzido no LipSync e clique em "Gerar Vídeo". Aguarde alguns instantes para o processamento.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">6. Baixe e compartilhe</h3>
                  <p className="text-muted-foreground">
                    Após a geração, baixe o vídeo e compartilhe em suas redes sociais como Pinterest, YouTube, ou em seu blog.
                  </p>
                </div>
              </CardContent>
              <CardFooter>
                <Button onClick={() => {
                  const element = document.querySelector('[data-value="create"]');
                  if (element instanceof HTMLElement) {
                    element.click();
                  }
                }}>
                  Vamos criar um vídeo <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </CardFooter>
            </Card>
          </TabsContent>

          <TabsContent value="create">
            <Card>
              <CardHeader>
                <CardTitle>Criar Vídeo com LipSync</CardTitle>
                <CardDescription>
                  Preencha os campos abaixo para criar seu vídeo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">1. Escolha o personagem</h3>
                  <Select value={selectedCharacter} onValueChange={setSelectedCharacter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um personagem" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Selecione um personagem</SelectItem>
                      {characters.map(char => (
                        <SelectItem key={char.id} value={char.id}>{char.name} ({char.type})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">2. Escreva seu texto em português</h3>
                  <Textarea 
                    placeholder="Digite seu texto em português (máximo 500 caracteres)" 
                    maxLength={500}
                    rows={4}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                  />
                  <div className="text-right text-xs text-muted-foreground">
                    {inputText.length}/500 caracteres
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">3. Traduza para inglês</h3>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      className="flex-1"
                      onClick={handleTranslate}
                    >
                      <Languages className="mr-2 h-4 w-4" /> Traduzir texto
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open("https://translate.google.com.br/?sl=es&tl=en&op=translate", "_blank")}
                    >
                      Abrir Google Tradutor
                    </Button>
                  </div>
                  <Textarea 
                    placeholder="Texto traduzido em inglês"
                    rows={4}
                    value={translatedText}
                    onChange={(e) => setTranslatedText(e.target.value)}
                  />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs" 
                    onClick={handleCopyText}
                  >
                    <Copy className="mr-1 h-3 w-3" /> Copiar texto
                  </Button>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">4. Gerar áudio (opcional)</h3>
                  <Button 
                    variant={isRecording ? "destructive" : "outline"} 
                    onClick={handleStartRecording} 
                    disabled={isRecording}
                  >
                    {isRecording ? (
                      <>Gravando... <PauseCircle className="ml-2 h-4 w-4 animate-pulse" /></>
                    ) : (
                      <>Gravar áudio <Mic className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">5. Gerar vídeo</h3>
                  <Button onClick={handleGenerateVideo}>
                    Gerar vídeo <FileVideo className="ml-2 h-4 w-4" />
                  </Button>
                </div>

                {videoUrl && (
                  <div className="space-y-2 border rounded-lg p-4">
                    <h3 className="text-lg font-semibold">Prévia do vídeo</h3>
                    <div className="relative aspect-video bg-black/10 rounded-md overflow-hidden">
                      <img 
                        src={videoUrl} 
                        alt="Prévia do vídeo gerado" 
                        className="w-full h-full object-cover"
                      />
                      <button 
                        className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                        onClick={handlePlayVideo}
                      >
                        {isPlaying ? (
                          <PauseCircle className="h-12 w-12 text-white" />
                        ) : (
                          <PlayCircle className="h-12 w-12 text-white" />
                        )}
                      </button>
                    </div>
                    <div className="flex gap-2 mt-4">
                      <Button variant="outline" onClick={handleDownloadVideo}>
                        <Download className="mr-2 h-4 w-4" /> Baixar vídeo
                      </Button>
                      <Button variant="outline" onClick={handleShareVideo}>
                        <Share2 className="mr-2 h-4 w-4" /> Compartilhar
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="share">
            <Card>
              <CardHeader>
                <CardTitle>Compartilhe seus vídeos</CardTitle>
                <CardDescription>
                  Aprenda como compartilhar os vídeos que você criou
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">1. Poste em seu blog</h3>
                  <p className="text-muted-foreground">
                    Você pode postar seus vídeos no seu blog, como por exemplo:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                    <li>
                      <a href="https://scriptjogobicho.blogspot.com/2025/08/videos-shorts-animais.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Blog - Vídeos Shorts Animais
                      </a>
                    </li>
                    <li>
                      <a href="https://scriptjogobicho.blogspot.com/2025/08/criando-audio-e-video-no-lipsync.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Blog - Criando Áudio e Vídeo no LipSync
                      </a>
                    </li>
                    <li>
                      <a href="https://scriptjogobicho.blogspot.com/2025/08/faucet-da-keranco-e-bagiconfaucetpay.html" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Blog - Faucet da Keranco e BagiconFaucetPay
                      </a>
                    </li>
                  </ul>
                  <Button variant="outline" className="mt-2">
                    <UploadCloud className="mr-2 h-4 w-4" /> Upload para o blog
                  </Button>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">2. Compartilhe no Pinterest</h3>
                  <p className="text-muted-foreground">
                    Crie playlists e compartilhe seus vídeos no Pinterest para maior alcance:
                  </p>
                  <a href="https://br.pinterest.com/crisgride/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline block">
                    Pinterest - Crisgride
                  </a>
                  <Button 
                    variant="outline" 
                    className="mt-2" 
                    onClick={() => handleShare("Pinterest")}
                  >
                    Compartilhar no Pinterest
                  </Button>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">3. Crie vídeos no Canva</h3>
                  <p className="text-muted-foreground">
                    Use o Canva para editar e melhorar seus vídeos antes de compartilhar:
                  </p>
                  <a href="https://www.canva.com/download/windows/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline block">
                    Baixar Canva para Windows
                  </a>
                  <Button 
                    variant="outline" 
                    className="mt-2" 
                    onClick={() => window.open("https://www.canva.com", "_blank")}
                  >
                    Abrir Canva
                  </Button>
                </div>

                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">4. Use o Adobe Express</h3>
                  <p className="text-muted-foreground">
                    Outra excelente ferramenta para edição de vídeos é o Adobe Express:
                  </p>
                  <a href="https://www.adobe.com/express/create/animation/youtube" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline block">
                    Adobe Express - Criação para YouTube
                  </a>
                  <Button 
                    variant="outline" 
                    className="mt-2" 
                    onClick={() => window.open("https://www.adobe.com/express", "_blank")}
                  >
                    Abrir Adobe Express
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={showShareOptions} onOpenChange={setShowShareOptions}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Compartilhar vídeo</DialogTitle>
              <DialogDescription>
                Escolha onde deseja compartilhar seu vídeo
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <Button 
                variant="outline" 
                className="flex flex-col h-24 items-center justify-center"
                onClick={() => handleShare("YouTube")}
              >
                <Youtube className="h-8 w-8 mb-2" />
                YouTube
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col h-24 items-center justify-center"
                onClick={() => handleShare("Pinterest")}
              >
                <span className="text-xl font-bold mb-2">P</span>
                Pinterest
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col h-24 items-center justify-center"
                onClick={() => handleShare("Blogger")}
              >
                <span className="text-xl font-bold mb-2">B</span>
                Blogger
              </Button>
              <Button 
                variant="outline" 
                className="flex flex-col h-24 items-center justify-center"
                onClick={() => handleShare("Instagram")}
              >
                <Instagram className="h-8 w-8 mb-2" />
                Instagram
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowShareOptions(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
      <Footer />
    </div>
  );
};

export default LipSyncPage;